// Temukan Paket (Simulasi Pluto v4 plutoOpportunities): the official portals
// to search, Catat Paket (the Pantau Paket record dialog), and the
// opportunities not yet made a project, each with Jadikan project and the
// official link. Shown on the Project Penyedia page and in the Persiapan
// Pemilihan stage of a paket. Only while the role has pantau_paket.
import { useState } from "react";
import * as API from "../api";
import type { WatchInput, WatchItem } from "../api";
import { formatRupiah } from "../lib/format";
import { formatDeadline } from "../project/stages";
import { navigate } from "../router";
import { useFeatures } from "../shell/features";
import { useProjects } from "../shell/Projects";
import { Notice, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { isActiveOpportunity, PORTALS } from "./shared";
import { WatchDialog } from "./WatchDialog";

/** Opportunities the section lists at most. */
export const OPPORTUNITY_ROWS = 9;

export function Opportunities({ headingLevel = 2 }: { headingLevel?: 2 | 3 }) {
  const features = useFeatures();
  const on = features.list !== null && features.has("pantau_paket");
  const list = useAsync(() => (on ? API.listWatchlist() : Promise.resolve([] as WatchItem[])), [on]);
  const projects = useProjects();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [making, setMaking] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  if (!on) return null;

  const all = list.data ?? [];
  const active = all.filter(isActiveOpportunity).slice(0, OPPORTUNITY_ROWS);
  const Heading = headingLevel === 2 ? "h2" : "h3";

  async function save(input: WatchInput) {
    setBusy(true);
    setFormError(null);
    try {
      list.set([await API.createWatchItem(input), ...all]);
      setOpen(false);
    } catch (err) {
      setFormError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function toProject(item: WatchItem) {
    setMaking(item.id);
    setFailure(null);
    try {
      const made = await API.watchItemToProject(item.id);
      list.set(all.map((x) => (x.id === item.id ? made.item : x)));
      projects?.list.reload();
      navigate(`/project/${made.project.id}`);
    } catch (err) {
      setFailure(`${item.nama} belum dijadikan project. ${API.errorMessage(err)}`);
    } finally {
      setMaking(null);
    }
  }

  return (
    <section className="pks" aria-labelledby="pks-opp-title" data-section="temukan-paket">
      <div className="pks-head">
        <div className="pks-head-text">
          <Heading id="pks-opp-title" className="pks-title">
            Cari Paket Tayang yang Ingin Diikuti
          </Heading>
          <p className="pks-lead">Pencarian dilakukan di portal pemerintah; paket yang diminati dicatat di Pluto.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          aria-haspopup="dialog"
          onClick={() => {
            setFormError(null);
            setOpen(true);
          }}
        >
          <Glyph name="add" size={16} />
          <span>Catat Paket</span>
        </button>
      </div>
      <ul className="pks-portals" aria-label="Portal resmi">
        {PORTALS.slice(0, 3).map((p) => (
          <li key={p.url}>
            <a className="pks-portal" href={p.url} target="_blank" rel="noopener noreferrer">
              <span className="pks-portal-name">
                <Glyph name="link" size={16} />
                <span>{p.name}</span>
              </span>
              <span className="pks-portal-desc">{p.desc}</span>
            </a>
          </li>
        ))}
      </ul>
      {failure ? (
        <Notice tone="danger" title="Belum berhasil">
          <p>{failure}</p>
        </Notice>
      ) : null}
      {list.error ? (
        <p className="pks-caption">Paket pantauan belum termuat. {API.errorMessage(list.error)}</p>
      ) : active.length === 0 ? (
        <p className="pks-caption">{list.data ? "Belum ada peluang tersimpan. Buka SiRUP atau SPSE, lalu klik Catat Paket atau Project baru." : "Memuat paket pantauan"}</p>
      ) : (
        <ul className="rows" aria-label="Peluang tersimpan">
          {active.map((x) => (
            <li key={x.id} className="row tp-row">
              <span className="row-main">
                <span className="row-title">{x.nama}</span>
                <span className="row-meta">
                  {[x.instansi || "Instansi belum dicatat", x.kode || "Kode belum dicatat", x.tenggat ? `Tenggat ${formatDeadline(x.tenggat)}` : "Tenggat periksa SPSE", x.pagu != null ? formatRupiah(x.pagu) : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="row-side">
                <button type="button" className="btn btn-secondary" disabled={making !== null} aria-busy={making === x.id || undefined} onClick={() => void toProject(x)}>
                  <Glyph name="folderAdd" />
                  <span>{making === x.id ? "Membuat project" : "Jadikan project"}</span>
                </button>
                {x.link ? (
                  <a className="btn btn-secondary" href={x.link} target="_blank" rel="noopener noreferrer nofollow" aria-label={`Lihat ${x.nama} di portal resmi`}>
                    <Glyph name="link" />
                    <span>Lihat</span>
                  </a>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <WatchDialog open={open} item={null} busy={busy} error={formError} onSubmit={(input) => void save(input)} onClose={() => !busy && setOpen(false)} />
    </section>
  );
}
